import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { approvePost, archivePost, createPost, getChannel, listPosts, postPreviewParts, postShareCode, updatePost, writePostWithAi } from '../lib/api';
import { channelLabel, ctaLabel, dateTime, friendlyError, copyText } from '../lib/format';
import { prefilledText, waLink } from '../lib/links';
import type { Post, PostChannel, PostCta, WhatsAppChannel } from '../lib/types';
import { Badge, Button, Empty, ErrorText, Modal, Notice, PageHeader, SelectField, TextArea, TextField } from '../components/ui';

const PURPOSES = ['New launch', 'Price update', 'Site-visit weekend', 'Offer', 'Possession update', 'NRI focus'];

export function Posts() {
  const { isManager, projects } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [channel, setChannel] = useState<WhatsAppChannel | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try { const [ps, ch] = await Promise.all([listPosts(), getChannel().catch(() => null)]); setPosts(ps); setChannel(ch); } catch (e) { setError(friendlyError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const projectName = (id: string) => projects.find((p) => p.id === id)?.name ?? 'Project';

  return (
    <div>
      <PageHeader title="Campaign Studio" subtitle="Turn approved project facts into WhatsApp campaigns, Instagram and Facebook ads, or everyday posts. Every draft is checked before sharing."
        action={isManager && <Button variant="primary" onClick={() => setCreating(true)}>Create with AI</Button>} />
      <ErrorText>{error}</ErrorText>
      {posts.length === 0 && <Empty title="No campaigns or posts yet">{isManager ? 'Choose a project with approved facts and ReAstra will prepare the copy.' : 'Your manager prepares campaign copy and posts for you to use.'}</Empty>}
      <ul className="flex flex-col gap-3">
        {posts.map((p) => <PostCard key={p.id} post={p} projectName={projectName(p.project_id)} channel={channel} isManager={isManager} onChanged={load} />)}
      </ul>
      {creating && <NewPostModal onClose={() => setCreating(false)} onSaved={async () => { setCreating(false); await load(); }} />}
    </div>
  );
}

export function PostCard({ post, projectName, channel, isManager, onChanged }: { post: Post; projectName: string; channel: WhatsAppChannel | null; isManager: boolean; onChanged: () => void }) {
  const [code, setCode] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(post.body);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const tone = post.status === 'ready' ? 'ok' : post.status === 'needs_review' ? 'warn' : 'bad';
  const isCampaign = post.channel === 'whatsapp_campaign';
  const isAd = post.channel === 'facebook_ad' || post.channel === 'instagram_ad';
  const adCopy = isAd ? Object.fromEntries(['Headline', 'Primary text', 'Description'].map((field) =>
    [field, post.body.match(new RegExp(`^${field}:\\s*(.+)$`, 'mi'))?.[1]?.trim() ?? ''])) : null;
  const label = { ready: isCampaign ? 'Copy ready · approval needed' : isAd ? 'Ad copy ready' : 'Ready to share', needs_review: 'Waiting for a manager', blocked: 'Blocked', archived: 'Archived' }[post.status];
  const link = code ? (channel ? waLink(channel.display_number, prefilledText(projectName, code)) : null) : null;
  const shareText = code ? `${post.body}\n\n${ctaLabel[post.cta]}${link ? `: ${link}` : ''}\n\n${post.footer}` : '';

  async function run(fn: () => Promise<unknown>) { setError(''); try { await fn(); onChanged(); } catch (e) { setError(friendlyError(e)); } }

  return (
    <li className="rounded-2xl border border-line bg-surface p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={tone}>{label}</Badge>
          <span className="font-medium">{projectName}</span>
          <span className="text-sm text-muted">{post.purpose} · {channelLabel[post.channel]} · {ctaLabel[post.cta]}{post.written_by === 'ai' ? ' · written by AI' : ''}</span>
        </div>
        <span className="text-xs text-muted">{dateTime(post.created_at)}</span>
      </div>
      {post.reasons.length > 0 && <p className={`mb-2 text-sm ${post.status === 'blocked' ? 'text-bad' : 'text-warn'}`}>{post.reasons.join(' · ')}</p>}
      {isCampaign && <p className="mb-3 rounded-lg bg-warnSoft px-3 py-2 text-xs text-warn">Marketing template candidate only. ReAstra does not broadcast it. Before sending, your firm must have the buyer's marketing opt-in and an approved Meta WhatsApp template.</p>}
      {isAd && <p className="mb-3 rounded-lg bg-accentSoft px-3 py-2 text-xs text-accent">Copy for Meta Ads Manager only — not a live ad. Use approved builder photos/video and review housing-ad requirements before publishing.</p>}
      {editing ? (
        <>
          <textarea aria-label="Post text" rows={5} value={body} onChange={(e) => setBody(e.target.value)} className="mb-2 w-full rounded-lg border border-line px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => run(async () => { await updatePost(post.id, body); setEditing(false); })}>Save and check</Button>
            <Button variant="ghost" onClick={() => { setBody(post.body); setEditing(false); }}>Cancel</Button>
          </div>
        </>
      ) : isAd && adCopy?.Headline && adCopy?.['Primary text'] && adCopy?.Description ? (
        <div className="overflow-hidden rounded-xl border border-line bg-surface">
          <div className="flex items-center justify-between border-b border-line px-3 py-2 text-xs text-muted">
            <span>Ad copy preview · {projectName}</span><span>Not published</span>
          </div>
          <div className="p-4">
            <p className="mb-2 text-xs uppercase tracking-wider text-accent">{channelLabel[post.channel]}</p>
            <h3 aria-label="Headline" className="font-display text-xl">{adCopy.Headline}</h3>
            <p aria-label="Primary text" className="mt-2 whitespace-pre-wrap text-sm">{adCopy['Primary text']}</p>
            <div className="mt-4 rounded-lg bg-surface2 px-3 py-2 text-sm">
              <p aria-label="Description" className="font-medium">{adCopy.Description}</p>
              <p className="mt-1 text-xs text-muted">CTA: {ctaLabel[post.cta]} · Add approved photo/video in Meta Ads Manager</p>
            </div>
            <p className="mt-3 text-xs text-muted">{post.footer}</p>
          </div>
        </div>
      ) : (
        <div className="rounded-xl bg-surface2 p-3 text-sm">
          <p className="whitespace-pre-wrap">{post.body}</p>
          <p className="mt-2 font-medium text-accent">👉 {ctaLabel[post.cta]}</p>
          <p className="mt-2 text-xs text-muted">{post.footer}</p>
        </div>
      )}
      {!editing && (
        <div className="mt-3 flex flex-wrap gap-2">
          {post.status === 'ready' && !code && <Button variant="primary" onClick={() => run(async () => setCode(await postShareCode(post.id)))}>{isAd || isCampaign ? 'Prepare my tracked copy' : 'Share with my link'}</Button>}
          {code && (
            <>
              <Button variant="primary" onClick={async () => { await copyText(shareText); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>{copied ? 'Copied' : isCampaign ? 'Copy template candidate' : isAd ? 'Copy ad copy' : 'Copy post'}</Button>
              {!isCampaign && !isAd && <a href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-lg border border-line px-3.5 py-2 text-sm font-medium hover:border-muted">Open WhatsApp</a>}
              {!channel && <Link to={`/simulator?code=${code}`} className="inline-flex items-center rounded-lg border border-line px-3.5 py-2 text-sm font-medium hover:border-muted">Test the link in the simulator</Link>}
            </>
          )}
          {isManager && post.status === 'needs_review' && <Button onClick={() => run(() => approvePost(post.id))}>Approve</Button>}
          {isManager && <Button variant="ghost" onClick={() => setEditing(true)}>Edit</Button>}
          {isManager && <Button variant="ghost" onClick={() => run(() => archivePost(post.id))}>Archive</Button>}
        </div>
      )}
      {code && <p className="mt-2 text-xs text-muted">Your attribution code is <span className="font-mono">{code}</span>. {link ? 'The copied text contains your tracked WhatsApp enquiry link.' : 'Connect the firm WhatsApp number to generate a working tracked link; for now, add a valid destination in the ad manager.'} {isAd ? 'Paste the three fields into the matching Meta ad fields and select the destination there.' : isCampaign ? 'Submit the candidate for Meta template approval; this button never sends to a contact list.' : 'For WhatsApp Status, paste the copied post as a Status.'}</p>}
      <div className="mt-2"><ErrorText>{error}</ErrorText></div>
    </li>
  );
}

function NewPostModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { projects } = useAuth();
  const ready = projects.filter((p) => p.status === 'approved');
  const [projectId, setProjectId] = useState(ready[0]?.id ?? '');
  const [purpose, setPurpose] = useState(PURPOSES[2]);
  const [focus, setFocus] = useState('');
  const [cta, setCta] = useState<PostCta>('site_visit');
  const [channel, setChannel] = useState<PostChannel>('whatsapp_campaign');
  const [language, setLanguage] = useState('English');
  const [manual, setManual] = useState(false);
  const [body, setBody] = useState('');
  const [parts, setParts] = useState<{ cta_line: string; footer: string; cta_problem: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (projectId) postPreviewParts(projectId, cta).then(setParts).catch(() => setParts(null)); }, [projectId, cta]);

  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try {
      if (manual) await createPost({ projectId, purpose, cta, channel, body });
      else await writePostWithAi({ projectId, purpose, focus, cta, channel, language });
      onSaved();
    } catch (err) {
      const msg = friendlyError(err);
      setError(msg);
      if (/not configured/i.test(msg)) setManual(true);
      setBusy(false);
    }
  }

  if (!ready.length) {
    return <Modal open onClose={onClose} title="Create campaign or ad"><p className="text-sm">No project has approved facts yet. <Link className="text-accent" to="/projects">Add facts to a project</Link> first; campaigns may only use approved facts.</p></Modal>;
  }
  return (
    <Modal open onClose={onClose} title="Create campaign or ad" wide>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField label="Project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            {ready.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </SelectField>
          <SelectField label="Purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
            {PURPOSES.map((p) => <option key={p}>{p}</option>)}
          </SelectField>
          <SelectField label="Call to action" value={cta} onChange={(e) => setCta(e.target.value as PostCta)}>
            {Object.entries(ctaLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </SelectField>
          <SelectField label="Format" value={channel} onChange={(e) => setChannel(e.target.value as PostChannel)}>
            <optgroup label="Campaigns & paid ads">
              {(['whatsapp_campaign', 'instagram_ad', 'facebook_ad'] as const).map((k) => <option key={k} value={k}>{channelLabel[k]}</option>)}
            </optgroup>
            <optgroup label="Everyday posts">
              {(['whatsapp_status', 'instagram', 'facebook', 'broker_group'] as const).map((k) => <option key={k} value={k}>{channelLabel[k]}</option>)}
            </optgroup>
          </SelectField>
        </div>
        {channel === 'whatsapp_campaign' && <p className="rounded-xl border border-warn bg-warnSoft p-3 text-sm text-warn">AI creates a WhatsApp marketing-template candidate, not a bulk send. Collect buyer opt-in and secure Meta template approval before any broadcast.</p>}
        {(channel === 'facebook_ad' || channel === 'instagram_ad') && <p className="rounded-xl border border-line bg-surface2 p-3 text-sm">AI prepares <strong>Headline</strong>, <strong>Primary text</strong> and <strong>Description</strong> for Meta Ads Manager. Use authentic builder-approved visuals; connecting and publishing an ad happens separately.</p>}
        {parts?.cta_problem && <ErrorText>{parts.cta_problem}. Choose another call to action or approve the facts first.</ErrorText>}
        <div className="flex gap-1 rounded-lg bg-surface2 p-1 text-sm font-medium">
          <button type="button" onClick={() => setManual(false)} className={`flex-1 rounded-md py-1.5 ${!manual ? 'bg-surface shadow' : 'text-muted'}`}>Write with AI</button>
          <button type="button" onClick={() => setManual(true)} className={`flex-1 rounded-md py-1.5 ${manual ? 'bg-surface shadow' : 'text-muted'}`}>Write it myself</button>
        </div>
        {manual ? (
          <TextArea label={channel === 'whatsapp_campaign' ? 'WhatsApp template draft' : channel.endsWith('_ad') ? 'Ad copy' : 'Post text'} required value={body} onChange={(e) => setBody(e.target.value)} hint="Leave out the RERA number and link: they are added for you. For paid ads, use Headline / Primary text / Description labels." />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Focus (optional)" value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="3 BHK in tower C" />
            <SelectField label="Language" value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option>English</option><option>Hindi</option><option>Hinglish</option>
            </SelectField>
          </div>
        )}
        {parts && <Notice>Every share ends with "👉 {parts.cta_line}: [the sharer's own link]" and this footer: {parts.footer}</Notice>}
        <ErrorText>{error}</ErrorText>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={busy || !!parts?.cta_problem}>{busy ? (manual ? 'Checking…' : 'Writing…') : manual ? 'Check and save' : 'Generate and save'}</Button>
        </div>
      </form>
    </Modal>
  );
}
